import { db } from '@/data/firebase/config';
import { ALLOWED_DOMAINS } from '@/domain/env/allowed-domains';
import { DOCUMENTS_DB } from '@/domain/env/documents-db';
import { User } from '@/domain/types/user';
import Button from '@/presentation/components/button';
import Table from '@/presentation/components/table';
import { collection, doc, getDocs, updateDoc } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import DomainsManager from './domains-manager';

import { Column } from '@/presentation/components/table';

type UserRow = User & { domain: string; action?: JSX.Element };

const columns: Column<UserRow>[] = [
  { header: 'Nombre', accessor: 'name' },
  { header: 'Apellido', accessor: 'lastname' },
  { header: 'Email', accessor: 'email' },
  { header: 'Dominio', accessor: 'domain' },
  {
    header: 'Estado',
    accessor: 'domain',
    render: (domain) =>
      domain && ALLOWED_DOMAINS.includes(domain as string)
        ? 'Activo'
        : 'Bloqueado',
  },
  { header: 'Acción', accessor: 'action', render: (value) => value },
];

function extractDomain(email?: string) {
  if (!email || typeof email !== 'string' || !email.includes('@')) return '';
  return email.split('@')[1] || '';
}

const UsersDomainAdminPage = () => {
  const handleBlock = async (user: User) => {
    if (!user.id) return;
    await updateDoc(doc(db, DOCUMENTS_DB.USERS, user.id), {
      isDomainBlocked: true,
      domainBlockedReason: 'Dominio no permitido',
      domainBlockedAt: new Date().toISOString(),
    });
    setUsers((prev) =>
      prev.map((u) =>
        u.id === user.id
          ? {
              ...u,
              isDomainBlocked: true,
              domainBlockedReason: 'Dominio no permitido',
              domainBlockedAt: new Date().toISOString(),
            }
          : u,
      ),
    );
  };
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<'all' | 'blocked' | 'unblocked'>('all');

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      const snapshot = await getDocs(collection(db, DOCUMENTS_DB.USERS));
      const usersData = snapshot.docs.map((docSnap) => {
        const data = docSnap.data() as User;
        return {
          ...data,
        };
      });
      setUsers(usersData);
      setLoading(false);
    };
    fetchUsers();
  }, []);

  const filteredUsers = users.filter((user) => {
    const domain = extractDomain(user.email);
    const isAllowed = ALLOWED_DOMAINS.includes(domain);
    if (filter === 'blocked') return !isAllowed;
    if (filter === 'unblocked') return isAllowed;
    return true;
  });

  const tableData = filteredUsers.map((user) => {
    const domain = extractDomain(user.email);
    return {
      ...user,
      domain,
      action: ALLOWED_DOMAINS.includes(domain) ? null : (
        <Button
          label="Bloquear"
          onClick={() => handleBlock(user)}
          variant="danger"
        />
      ),
    };
  });

  const [tab, setTab] = useState<'dominios' | 'usuarios'>('dominios');
  return (
    <div className="p-4 flex flex-col gap-8">
      <h1 className="text-2xl font-bold mb-4">
        Gestión de usuarios por dominio
      </h1>
      <div className="flex gap-4 mb-4">
        <Button
          label="Dominios"
          variant={tab === 'dominios' ? 'primary' : 'secondary'}
          onClick={() => setTab('dominios')}
        />
        <Button
          label="Usuarios"
          variant={tab === 'usuarios' ? 'primary' : 'secondary'}
          onClick={() => setTab('usuarios')}
        />
      </div>
      {tab === 'dominios' && <DomainsManager />}
      {tab === 'usuarios' && (
        <div>
          <h2 className="text-xl font-bold mb-2">
            Usuarios con dominio no permitido
          </h2>
          <div className="flex gap-2 mb-4">
            <Button
              label="Todos"
              onClick={() => setFilter('all')}
              variant={filter === 'all' ? 'primary' : 'secondary'}
            />
            <Button
              label="Solo bloqueados"
              onClick={() => setFilter('blocked')}
              variant={filter === 'blocked' ? 'primary' : 'secondary'}
            />
            <Button
              label="En lista blanca"
              onClick={() => setFilter('unblocked')}
              variant={filter === 'unblocked' ? 'primary' : 'secondary'}
            />
          </div>
          {loading ? (
            <div>Cargando usuarios...</div>
          ) : (
            <Table columns={columns} data={tableData as any} />
          )}
        </div>
      )}
    </div>
  );
};

export default UsersDomainAdminPage;
